import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { UnauthorizedError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { FormField } from '../components/ui/FormField';

// The form skips the browser's checks (noValidate) to show one message in one
// place, so the length the inputs declare is enforced here. The platform
// itself would take a shorter one.
const MIN_PASSWORD_LENGTH = 8;

export function ChangePasswordPage() {
  const { changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setPending(true);
    try {
      await changePassword(password, confirmation);
      navigate('/', { replace: true });
    } catch (err) {
      // The session is gone — expired, or the account lost access — and no
      // retry here can bring it back: the way on is signing in again.
      if (err instanceof UnauthorizedError) {
        await signOut();
        return;
      }
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 shadow-sm flex flex-col gap-4"
        noValidate
      >
        <header className="mb-2">
          <h1 className="text-xl font-semibold">Set a new password</h1>
          <p className="text-sm text-[var(--co-muted)]">
            You must change the temporary password before continuing.
          </p>
        </header>

        <FormField label="New password" htmlFor="cp-new">
          <Input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>

        <FormField
          label="Confirm password"
          htmlFor="cp-confirm"
          error={error ?? undefined}
        >
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </FormField>

        <Button type="submit" disabled={pending}>
          {pending ? 'Updating…' : 'Update password'}
        </Button>
        <Button type="button" variant="ghost" disabled={pending} onClick={() => void signOut()}>
          Sign out
        </Button>
      </form>
    </main>
  );
}
